import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { AlertTriangle, CheckCircle2, ClipboardList, RefreshCw } from 'lucide-react';
import { getLscCommissionerDigest } from '../api';
import { lscPath } from '../routing';
import type {
  LscCommissionerDigest,
  LscCommissionerDigestCandidateSample,
  LscCommissionerDigestCounts,
  LscCommissionerDigestSampleCategory,
  LscCommissionerDigestSamples,
  LscCommissionerDigestSyncRunSample,
} from '../types';

export type CommissionerDigestCardProps = {
  leagueId: string;
  hideTitle?: boolean;
};

export const DIGEST_SAMPLE_CATEGORY_ORDER: LscCommissionerDigestSampleCategory[] = [
  'new_synced',
  'synced_unmatched',
  'pending_review',
  'needs_identity_attestation',
  'ready_to_publish',
];

export const DIGEST_SAMPLE_CATEGORY_LABELS: Record<LscCommissionerDigestSampleCategory, string> = {
  new_synced: 'Sync activity (7d)',
  synced_unmatched: 'Unmatched',
  pending_review: 'Pending review',
  needs_identity_attestation: 'Needs identity',
  ready_to_publish: 'Ready to publish',
};

export const DIGEST_COUNT_LABELS: { key: keyof LscCommissionerDigestCounts; label: string }[] = [
  { key: 'new_synced', label: 'Sync activity (7d)' },
  { key: 'synced_unmatched', label: 'Unmatched' },
  { key: 'pending_review', label: 'Needs review' },
  { key: 'needs_identity_attestation', label: 'Needs identity review' },
  { key: 'ready_to_publish', label: 'Ready to publish' },
  { key: 'recently_published', label: 'Published' },
  { key: 'unavailable_count', label: 'Unavailable replays' },
  { key: 'member_twitch_issues', label: 'Twitch issues' },
];

export type DigestCountLinkTarget =
  | { kind: 'vods'; filter: string; availability?: string }
  | { kind: 'needs_attention' }
  | { kind: 'archive' }
  | { kind: 'none' };

export const DIGEST_COUNT_LINK_TARGETS: Record<
  keyof LscCommissionerDigestCounts,
  DigestCountLinkTarget
> = {
  new_synced: { kind: 'vods', filter: 'sync_activity' },
  synced_unmatched: { kind: 'vods', filter: 'unmatched' },
  pending_review: { kind: 'needs_attention' },
  needs_identity_attestation: { kind: 'needs_attention' },
  ready_to_publish: { kind: 'needs_attention' },
  recently_published: { kind: 'archive' },
  unavailable_count: { kind: 'vods', filter: 'all', availability: 'unavailable' },
  member_twitch_issues: { kind: 'none' },
};

export function digestCountHref(leagueId: string, key: keyof LscCommissionerDigestCounts): string | null {
  const target = DIGEST_COUNT_LINK_TARGETS[key];
  if (target.kind === 'vods') {
    const params = new URLSearchParams();
    if (target.filter !== 'all') {
      params.set('filter', target.filter);
    }
    if (target.availability) {
      params.set('availability', target.availability);
    }
    const qs = params.toString();
    return lscPath(`/leagues/${leagueId}/vods${qs ? `?${qs}` : ''}`);
  }
  if (target.kind === 'needs_attention') {
    return lscPath(`/leagues/${leagueId}/needs-attention`);
  }
  if (target.kind === 'archive') {
    return lscPath(`/leagues/${leagueId}/archive`);
  }
  return null;
}

function formatDigestTimestamp(value?: string | null): string {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString();
}

export function isDigestCountsEmpty(counts: LscCommissionerDigestCounts): boolean {
  return Object.values(counts).every((value) => value === 0);
}

export function formatDigestSampleStatus(sample: LscCommissionerDigestCandidateSample): string {
  const parts: string[] = [];
  if (sample.match_status) parts.push(`match: ${sample.match_status}`);
  if (sample.review_status) parts.push(`review: ${sample.review_status}`);
  if (sample.archive_status) parts.push(`archive: ${sample.archive_status}`);
  return parts.length > 0 ? parts.join(' · ') : 'status pending';
}

export function collectDigestAttentionSamples(
  samples: LscCommissionerDigestSamples,
  sampleLimit = 5
): Array<{ category: LscCommissionerDigestSampleCategory; sample: LscCommissionerDigestCandidateSample }> {
  const rows: Array<{
    category: LscCommissionerDigestSampleCategory;
    sample: LscCommissionerDigestCandidateSample;
  }> = [];
  for (const category of DIGEST_SAMPLE_CATEGORY_ORDER) {
    const categorySamples = samples[category] ?? [];
    for (const sample of categorySamples) {
      rows.push({ category, sample });
      if (rows.length >= sampleLimit) {
        return rows;
      }
    }
  }
  return rows;
}

function schedulerRunFailed(run: LscCommissionerDigestSyncRunSample | null | undefined): boolean {
  if (!run?.status) return false;
  const status = run.status.trim().toLowerCase();
  return status === 'failed' || status === 'error';
}

function formatSchedulerRunSummary(run: LscCommissionerDigestSyncRunSample | null | undefined): string {
  if (!run) return 'No scheduler run recorded yet';
  const status = run.status ? run.status.replace(/_/g, ' ') : 'unknown';
  const videosSeen = run.videos_seen ?? 0;
  return `${status}, ${videosSeen} VOD${videosSeen === 1 ? '' : 's'} seen`;
}

function candidateDetailHref(leagueId: string, candidateId: string): string {
  return lscPath(`/leagues/${leagueId}/vods/${encodeURIComponent(candidateId)}`);
}

export function CommissionerDigestCardView({
  leagueId,
  digest,
  loading,
  error,
  onRefresh,
  hideTitle = false,
}: {
  leagueId: string;
  digest: LscCommissionerDigest | null;
  loading: boolean;
  error: string | null;
  onRefresh: () => void;
  hideTitle?: boolean;
}) {
  const counts = digest?.counts;
  const automation = digest?.automation;
  const attentionSamples = useMemo(
    () => (digest ? collectDigestAttentionSamples(digest.samples, digest.sample_limit ?? 5) : []),
    [digest]
  );
  const emptyCounts = counts ? isDigestCountsEmpty(counts) : false;
  const latestRun = automation?.latest_scheduler_run;
  const runFailed = schedulerRunFailed(latestRun);
  const dryRunMonitoring =
    Boolean(automation?.scheduled_enabled) && !automation?.scheduled_apply_enabled;
  const autoApplyOff = !automation?.scheduled_apply_enabled;

  return (
    <div className="card space-y-4" data-testid="commissioner-digest-card">
      <div className="flex items-start justify-between gap-3">
        {!hideTitle ? (
          <div>
            <h2 className="text-lg font-semibold text-white">League status</h2>
            <p className="text-sm text-secondary-400 mt-1">
              Recent sync activity and review counts for the last {digest?.window_hours ?? 168} hours.
            </p>
          </div>
        ) : (
          <div className="flex-1" />
        )}
        <div className="flex items-center gap-2 shrink-0">
          <ClipboardList className="h-5 w-5 text-primary-400" aria-hidden />
          <button
            type="button"
            onClick={onRefresh}
            className="btn-secondary inline-flex items-center gap-2 text-sm"
            aria-label="Refresh league status"
            data-testid="commissioner-digest-refresh"
          >
            <RefreshCw className="h-4 w-4" aria-hidden />
            Refresh
          </button>
        </div>
      </div>

      {loading ? (
        <div className="space-y-2" data-testid="commissioner-digest-loading">
          <div className="h-4 w-2/3 rounded bg-secondary-700/60 animate-pulse" />
          <div className="h-4 w-1/2 rounded bg-secondary-700/40 animate-pulse" />
          <p className="text-sm text-secondary-400">Loading commissioner digest...</p>
        </div>
      ) : error ? (
        <div
          className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 space-y-2"
          data-testid="commissioner-digest-error"
        >
          <p className="text-sm text-red-200">{error}</p>
          <p className="text-sm text-secondary-300">
            Digest unavailable. Core league tools are still available.
          </p>
          <button type="button" onClick={onRefresh} className="btn-secondary text-sm">
            Retry
          </button>
        </div>
      ) : digest ? (
        <>
          <div
            className={`rounded-lg border px-4 py-3 space-y-3 ${
              runFailed
                ? 'border-amber-500/40 bg-amber-500/10'
                : 'border-secondary-700/60 bg-secondary-900/40'
            }`}
            data-testid="commissioner-digest-automation"
          >
            <p className="text-sm text-secondary-200">
              Scheduled VOD checks are running in preview mode. Automatic updates and auto-publish are off.
            </p>
            <dl className="grid gap-3 sm:grid-cols-2 text-sm">
              <div>
                <dt className="text-secondary-500">Preview monitoring</dt>
                <dd
                  className={dryRunMonitoring ? 'text-emerald-300' : 'text-secondary-100'}
                  data-testid="commissioner-digest-dry-run"
                >
                  {dryRunMonitoring ? 'On' : 'Off'}
                </dd>
              </div>
              <div>
                <dt className="text-secondary-500">Automatic updates</dt>
                <dd
                  className={autoApplyOff ? 'text-emerald-300' : 'text-amber-200'}
                  data-testid="commissioner-digest-auto-apply"
                >
                  {automation?.scheduled_apply_enabled ? 'On' : 'Off'}
                </dd>
              </div>
              <div className="sm:col-span-2">
                <dt className="text-secondary-500">Last automated check</dt>
                <dd className="text-secondary-100 flex items-center gap-2 flex-wrap">
                  {runFailed ? (
                    <AlertTriangle className="h-4 w-4 text-amber-300 shrink-0" aria-hidden />
                  ) : latestRun ? (
                    <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0" aria-hidden />
                  ) : null}
                  <span data-testid="commissioner-digest-last-run">
                    {formatSchedulerRunSummary(latestRun)}
                  </span>
                  {latestRun?.started_at ? (
                    <span className="text-secondary-400">
                      · {formatDigestTimestamp(latestRun.started_at)}
                    </span>
                  ) : null}
                </dd>
              </div>
              {latestRun ? (
                <>
                  <div>
                    <dt className="text-secondary-500">Candidates created</dt>
                    <dd className="text-secondary-100">{latestRun.candidates_created ?? 0}</dd>
                  </div>
                  <div>
                    <dt className="text-secondary-500">Candidates updated</dt>
                    <dd className="text-secondary-100">{latestRun.candidates_updated ?? 0}</dd>
                  </div>
                </>
              ) : null}
            </dl>
            {automation?.guardrail_summary ? (
              <p className="text-xs text-secondary-400">{automation.guardrail_summary}</p>
            ) : null}
            {latestRun && (latestRun.warnings_count ?? 0) + (latestRun.errors_count ?? 0) > 0 ? (
              <p className="text-xs text-amber-200" data-testid="commissioner-digest-run-warnings">
                Latest run reported {latestRun.warnings_count ?? 0} warning
                {(latestRun.warnings_count ?? 0) === 1 ? '' : 's'} and {latestRun.errors_count ?? 0}{' '}
                error{(latestRun.errors_count ?? 0) === 1 ? '' : 's'}.
              </p>
            ) : null}
          </div>

          {counts ? (
            <div
              className="flex flex-wrap gap-2"
              data-testid="commissioner-digest-count-chips"
            >
              {DIGEST_COUNT_LABELS.map(({ key, label }) => {
                const href = digestCountHref(leagueId, key);
                const chipLabel = `${label}: ${counts[key] ?? 0}`;
                if (href && (counts[key] ?? 0) > 0) {
                  return (
                    <Link
                      key={key}
                      to={href}
                      className="inline-flex rounded-full border border-secondary-600 bg-secondary-800/60 px-2.5 py-1 text-xs text-primary-200 hover:text-primary-100 hover:border-primary-600"
                      data-testid={`commissioner-digest-count-${key}`}
                    >
                      {chipLabel}
                    </Link>
                  );
                }
                return (
                  <span
                    key={key}
                    className="inline-flex rounded-full border border-secondary-600 bg-secondary-800/60 px-2.5 py-1 text-xs text-secondary-200"
                    data-testid={`commissioner-digest-count-${key}`}
                  >
                    {chipLabel}
                  </span>
                );
              })}
            </div>
          ) : null}

          {emptyCounts ? (
            <div data-testid="commissioner-digest-empty">
              <p className="text-sm text-secondary-200">
                No new archive items need attention in the last 7 days.
              </p>
            </div>
          ) : attentionSamples.length > 0 ? (
            <div className="space-y-2" data-testid="commissioner-digest-samples">
              <h3 className="text-sm font-medium text-white">Top items needing attention</h3>
              <ul className="divide-y divide-secondary-700/60 rounded-lg border border-secondary-700/60">
                {attentionSamples.map(({ category, sample }) => {
                  const candidateId = sample.candidate_id;
                  const title = sample.title || 'Untitled VOD';
                  const broadcaster = sample.broadcaster_login
                    ? `@${sample.broadcaster_login}`
                    : 'Unknown broadcaster';
                  const timestamp = sample.updated_at || sample.created_at;
                  return (
                    <li
                      key={`${category}-${candidateId ?? title}`}
                      className="px-3 py-2 flex flex-wrap items-center justify-between gap-2 text-sm"
                      data-testid="commissioner-digest-sample-row"
                    >
                      <div className="min-w-0 space-y-0.5">
                        <p className="text-secondary-100 truncate">{title}</p>
                        <p className="text-xs text-secondary-400">
                          {DIGEST_SAMPLE_CATEGORY_LABELS[category]} · {broadcaster} ·{' '}
                          {formatDigestSampleStatus(sample)}
                          {timestamp ? ` · ${formatDigestTimestamp(timestamp)}` : ''}
                        </p>
                      </div>
                      {candidateId ? (
                        <Link
                          to={candidateDetailHref(leagueId, candidateId)}
                          className="text-xs text-primary-300 hover:text-primary-200 shrink-0"
                          data-testid="commissioner-digest-sample-link"
                        >
                          View candidate
                        </Link>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : null}

          {digest.disclaimers.length > 0 ? (
            <p className="text-xs text-secondary-500" data-testid="commissioner-digest-disclaimers">
              {digest.disclaimers.join(' ')}
            </p>
          ) : null}

          <div
            className="flex flex-wrap gap-3 pt-1 border-t border-secondary-700/60"
            data-testid="commissioner-digest-footer-links"
          >
            <Link
              to={lscPath(`/leagues/${leagueId}/needs-attention`)}
              className="text-sm text-primary-300 hover:text-primary-200"
            >
              Open Needs Attention
            </Link>
            <Link
              to={lscPath(`/leagues/${leagueId}/archive`)}
              className="text-sm text-primary-300 hover:text-primary-200"
            >
              View archive
            </Link>
            <Link
              to={lscPath(`/leagues/${leagueId}/import`)}
              className="text-sm text-primary-300 hover:text-primary-200"
            >
              Import schedule
            </Link>
          </div>
        </>
      ) : null}
    </div>
  );
}

const CommissionerDigestCard: React.FC<CommissionerDigestCardProps> = ({
  leagueId,
  hideTitle = false,
}) => {
  const [digest, setDigest] = useState<LscCommissionerDigest | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadDigest = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getLscCommissionerDigest(leagueId, {
        window_hours: 168,
        sample_limit: 5,
      });
      setDigest(data);
    } catch {
      setDigest(null);
      setError('Could not load commissioner digest.');
    } finally {
      setLoading(false);
    }
  }, [leagueId]);

  useEffect(() => {
    void loadDigest();
  }, [loadDigest]);

  return (
    <CommissionerDigestCardView
      leagueId={leagueId}
      digest={digest}
      loading={loading}
      error={error}
      hideTitle={hideTitle}
      onRefresh={() => {
        void loadDigest();
      }}
    />
  );
};

export default CommissionerDigestCard;
